import React from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Users, ExternalLink, User, DollarSign } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card/Card';
import type { EventDTO } from '@/services/EventsService';
import { formatEventDates } from '@/lib/dateFormatters';

interface EventInfoCardProps {
  event: EventDTO;
  attendeeCount: number;
}

// Format budget range (no decimals, thousand separators)
function formatBudgetRange(min: number | null | undefined, max: number | null | undefined): string {
  if (!min && !max) return '';
  const formatBudget = (value: number): string => {
    return Math.round(value).toLocaleString('en-US');
  };
  const formattedMin = min ? formatBudget(min) : '';
  const formattedMax = max ? formatBudget(max) : '';
  if (formattedMin && formattedMax) {
    return `$${formattedMin} - $${formattedMax}`;
  } else if (formattedMin) {
    return `$${formattedMin}+`;
  } else if (formattedMax) {
    return `Up to $${formattedMax}`;
  }
  return '';
}

export function EventInfoCard({ event, attendeeCount }: EventInfoCardProps) {
  const hasDateInfo = !!(event.start_date || event.end_date || event.event_month || event.date_display_override);
  const hasExpectedAttendees = !!event.expected_attendees;
  const hasBudget = !!(event.budget_min || event.budget_max);
  const hasEventDetails = hasDateInfo || hasExpectedAttendees || hasBudget;

  return (
    <Card>
      <CardHeader>
        <CardTitle as="h3">Event Details</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-accent-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <Calendar className="w-5 h-5 text-accent-600" />
            </div>
            <div className="flex-1 min-w-0">
              <h4 className="font-semibold">{event.name}</h4>
              {event.description && (
                <p className="text-sm text-muted-foreground mt-1">{event.description}</p>
              )}
            </div>
          </div>

          {/* Event Details Section */}
          {hasEventDetails && (
            <div className="space-y-2 pt-2 border-t">
              {hasDateInfo && (
                <div className="flex items-start gap-2 text-sm">
                  <Calendar className="w-4 h-4 text-muted-foreground flex-shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <div className="text-xs text-muted-foreground">Date</div>
                    <div className="font-medium text-gray-900">{formatEventDates(event, false)}</div>
                  </div>
                </div>
              )}
              
              {hasExpectedAttendees && event.expected_attendees != null && (
                <div className="flex items-start gap-2 text-sm">
                  <User className="w-4 h-4 text-muted-foreground flex-shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <div className="text-xs text-muted-foreground">Expected Attendees</div>
                    <div className="font-medium text-gray-900">{event.expected_attendees.toLocaleString()}</div>
                  </div>
                </div>
              )}
              
              {hasBudget && (
                <div className="flex items-start gap-2 text-sm">
                  <DollarSign className="w-4 h-4 text-muted-foreground flex-shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <div className="text-xs text-muted-foreground">Budget</div>
                    <div className="font-medium text-gray-900">{formatBudgetRange(event.budget_min, event.budget_max)}</div>
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="flex items-center gap-2 text-sm pt-2 border-t">
            <Users className="w-4 h-4 text-muted-foreground" />
            <span>
              {attendeeCount > 0 ? (
                <>
                  <span className="font-medium">{attendeeCount}</span> attendee
                  {attendeeCount !== 1 && 's'}
                </>
              ) : (
                <span className="text-muted-foreground">No attendees added</span>
              )}
            </span>
            {attendeeCount > 0 && (
              <Link
                to={`/organization/events/${event.id}/attendees`}
                className="ml-auto text-primary-600 hover:text-primary-700 hover:underline inline-flex items-center gap-1"
              >
                View List
                <ExternalLink className="w-3 h-3" />
              </Link>
            )}
          </div>

          <Link
            to={`/organization/events/${event.id}`}
            className="inline-flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 hover:underline"
          >
            View Event Details
            <ExternalLink className="w-3 h-3" />
          </Link>
        </div>
      </CardContent>
    </Card>
  );
}

